import { useEffect, useState, useMemo } from "react";
import { Plus, Save, Trash2, Edit3, X, Receipt, Calendar, FileText, Download, Share2, Copy, Check } from "lucide-react";
import { api, API, formatErr } from "@/lib/api";

const STATUS_LABELS = {
  draft: "Borrador", sent: "Enviada", partial: "Parcial",
  paid: "Pagada", overdue: "Vencida", cancelled: "Cancelada",
};
const STATUS_CLS = {
  draft: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  sent: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  partial: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  paid: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  overdue: "bg-red-500/15 text-red-500 border-red-500/30",
  cancelled: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",
};

export default function InvoicesPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState({ status: "", client_id: "" });
  const [kpis, setKpis] = useState(null);
  const [currency, setCurrency] = useState("BOB");
  const [shareInv, setShareInv] = useState(null);

  const load = () => {
    const qs = new URLSearchParams();
    if (filter.status) qs.set("status", filter.status);
    if (filter.client_id) qs.set("client_id", filter.client_id);
    api.get(`/admin/com/invoices${qs.toString() ? "?" + qs : ""}`).then(setItems).catch(() => setItems([]));
    api.get(`/admin/com/invoices/summary/kpis?currency=${currency}`).then(setKpis).catch(() => setKpis(null));
  };
  useEffect(() => {
    load();
    api.get("/admin/com/clients").then(setClients).catch(() => setClients([]));
    api.get("/admin/proposals").then(setProposals).catch(() => setProposals([]));
    // eslint-disable-next-line
  }, [filter.status, filter.client_id, currency]);

  const totals = useMemo(() => {
    if (!editing) return { sub: 0, tax: 0, total: 0 };
    const sub = (editing.items || []).reduce((acc, it) => acc + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
    const tax = sub * (Number(editing.tax_pct) || 0) / 100;
    return { sub: Math.round(sub * 100) / 100, tax: Math.round(tax * 100) / 100, total: Math.round((sub + tax) * 100) / 100 };
  }, [editing]);

  const save = async () => {
    setError("");
    try {
      const body = { ...editing };
      if (editing.id && items.find((x) => x.id === editing.id)) {
        await api.put(`/admin/com/invoices/${editing.id}`, body);
      } else {
        delete body.id;
        await api.post("/admin/com/invoices", body);
      }
      setEditing(null); load();
    } catch (e) { setError(formatErr(e.response?.data?.detail) || "Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar factura?")) return;
    await api.delete(`/admin/com/invoices/${id}`);
    load();
  };

  const addPayment = async (invoice, amount, method, reference) => {
    if (!amount) return;
    await api.post(`/admin/com/invoices/${invoice.id}/payments`, { amount: Number(amount), method, reference });
    load();
    if (editing && editing.id === invoice.id) {
      const fresh = await api.get(`/admin/com/invoices/${invoice.id}`);
      setEditing(fresh);
    }
  };

  const removePayment = async (invoice, paymentId) => {
    if (!window.confirm("¿Revertir este pago?")) return;
    await api.delete(`/admin/com/invoices/${invoice.id}/payments/${paymentId}`);
    load();
    if (editing && editing.id === invoice.id) {
      const fresh = await api.get(`/admin/com/invoices/${invoice.id}`);
      setEditing(fresh);
    }
  };

  const newInvoice = () => setEditing({
    client_id: "", client_name: "", proposal_id: "", currency: "BOB",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: "", items: [], tax_pct: 13, payment_terms: "50% adelanto / 50% al cierre", status: "draft",
  });

  const importFromProposal = (proposalId) => {
    const pr = proposals.find((p) => p.id === proposalId);
    if (!pr || !editing) return;
    const items = (pr.packages || []).map((pk) => ({
      description: pk.name + (pk.frequency ? ` · ${pk.frequency}` : ""),
      qty: 1,
      unit_price: Number(pk.price) || 0,
    }));
    setEditing({
      ...editing,
      proposal_id: pr.id,
      client_id: pr.client_id || editing.client_id,
      client_name: pr.client_name || editing.client_name,
      brand_id: pr.brand_id || "",
      agency_id: pr.agency_id || "",
      currency: pr.currency || editing.currency,
      items: items.length ? items : editing.items,
      payment_terms: pr.payment_terms || editing.payment_terms,
    });
  };

  return (
    <div data-testid="invoices-page">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1 flex items-center gap-3"><Receipt className="w-8 h-8 text-primary" /> Cobranza · Facturación</h1>
          <p className="text-sm text-muted-foreground">Facturas, pagos parciales, KPIs · Total: {items.length}</p>
        </div>
        <button onClick={newInvoice} data-testid="invoice-new" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> Nueva factura
        </button>
      </div>

      <details className="mb-4 text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">⚙ Mantenimiento avanzado</summary>
        <div className="mt-2 p-3 border border-border rounded-sm bg-secondary/10 flex items-center gap-3 flex-wrap">
          <span>Migrar USD → BOB en paquetes/productos/talentos/propuestas/facturas:</span>
          <button onClick={async () => {
            const dry = await api.post("/admin/com/migrate/currency-bob?dry_run=true", {});
            if (!window.confirm(`¿Migrar a BOB? Se afectarán: ${JSON.stringify(dry.affected, null, 2)}`)) return;
            const r = await api.post("/admin/com/migrate/currency-bob", {});
            alert(`Migración OK: ${JSON.stringify(r.affected, null, 2)}`);
            load();
          }} data-testid="migrate-bob" className="px-3 py-1.5 border border-amber-500/50 text-amber-500 rounded-sm font-semibold hover:bg-amber-500/10">Ejecutar migración</button>
        </div>
      </details>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KpiCard label="Facturado" value={`${currency} ${(kpis?.total_billed || 0).toLocaleString()}`} />
        <KpiCard label="Cobrado" value={`${currency} ${(kpis?.total_paid || 0).toLocaleString()}`} cls="text-emerald-500 border-emerald-500/30" />
        <KpiCard label="Pendiente" value={`${currency} ${(kpis?.total_pending || 0).toLocaleString()}`} cls="text-amber-500 border-amber-500/30" />
        <KpiCard label="Vencido" value={`${currency} ${(kpis?.total_overdue || 0).toLocaleString()}`} cls="text-red-500 border-red-500/30" />
        <div className="border border-border rounded-sm p-4">
          <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Moneda</p>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm">
            <option value="BOB">BOB</option>
            <option value="USD">USD</option>
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Estado</label>
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="h-9 px-2 bg-background border border-border rounded-sm text-sm">
            <option value="">Todos</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Cliente</label>
          <select value={filter.client_id} onChange={(e) => setFilter({ ...filter, client_id: e.target.value })} className="h-9 px-2 bg-background border border-border rounded-sm text-sm min-w-[180px]">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {editing && (
        <div className="border border-primary rounded-sm p-6 mb-6 bg-secondary/10" data-testid="invoice-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">{editing.id ? `Editar ${editing.code || ""}` : "Nueva factura"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Cliente</label>
              <select value={editing.client_id || ""} onChange={(e) => {
                const c = clients.find((x) => x.id === e.target.value);
                setEditing({ ...editing, client_id: e.target.value, client_name: c?.name || editing.client_name });
              }} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
                <option value="">— Seleccionar —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Nombre visible cliente</label>
              <input value={editing.client_name || ""} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Importar desde propuesta</label>
              <select value={editing.proposal_id || ""} onChange={(e) => importFromProposal(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" data-testid="invoice-import-proposal">
                <option value="">— Seleccionar propuesta —</option>
                {proposals.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.client_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Fecha emisión</label>
              <input type="date" value={editing.issue_date || ""} onChange={(e) => setEditing({ ...editing, issue_date: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Fecha vencimiento</label>
              <input type="date" value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Moneda</label>
              <select value={editing.currency || "BOB"} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm">
                <option value="BOB">BOB</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Términos de pago</label>
              <input value={editing.payment_terms || ""} onChange={(e) => setEditing({ ...editing, payment_terms: e.target.value })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">IVA / Impuesto %</label>
              <input type="number" step="0.5" value={editing.tax_pct ?? 13} onChange={(e) => setEditing({ ...editing, tax_pct: Number(e.target.value) })} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm" />
            </div>
          </div>

          {/* Items */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-display font-bold text-sm uppercase tracking-widest">Detalle ({(editing.items || []).length})</h4>
              <button onClick={() => setEditing({ ...editing, items: [...(editing.items || []), { description: "", qty: 1, unit_price: 0 }] })} className="text-xs px-3 py-1.5 border border-border rounded-sm font-semibold hover:border-primary inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Línea</button>
            </div>
            {(editing.items || []).map((it, i) => (
              <div key={i} className="flex gap-2 items-end mb-2 border border-border rounded-sm p-2.5 bg-background">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Descripción</label>
                  <input value={it.description || ""} onChange={(e) => { const a = [...editing.items]; a[i] = { ...it, description: e.target.value }; setEditing({ ...editing, items: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" />
                </div>
                <div className="w-20">
                  <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Cant.</label>
                  <input type="number" min="1" value={it.qty || 1} onChange={(e) => { const a = [...editing.items]; a[i] = { ...it, qty: Number(e.target.value) }; setEditing({ ...editing, items: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" />
                </div>
                <div className="w-32">
                  <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">P. unit</label>
                  <input type="number" step="0.01" value={it.unit_price || 0} onChange={(e) => { const a = [...editing.items]; a[i] = { ...it, unit_price: Number(e.target.value) }; setEditing({ ...editing, items: a }); }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" />
                </div>
                <div className="w-28 text-right">
                  <p className="text-[10px] uppercase tracking-widest font-bold mb-1">Subtotal</p>
                  <p className="font-display font-black h-9 flex items-center justify-end">{((Number(it.qty) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}</p>
                </div>
                <button onClick={() => setEditing({ ...editing, items: editing.items.filter((_, x) => x !== i) })} className="p-1.5 text-destructive hover:bg-destructive/10 rounded-sm"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {(editing.items || []).length === 0 && <p className="text-xs italic text-muted-foreground">Agregá al menos una línea, o importá desde propuesta.</p>}
          </div>

          {/* Totales */}
          <div className="grid grid-cols-3 gap-3 border-t border-border pt-4 mb-4">
            <div><p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Subtotal</p><p className="font-display font-black text-xl">{editing.currency || "BOB"} {totals.sub.toLocaleString()}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Impuesto ({editing.tax_pct || 0}%)</p><p className="font-display font-black text-xl">{editing.currency || "BOB"} {totals.tax.toLocaleString()}</p></div>
            <div><p className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">Total</p><p className="font-display font-black text-2xl text-primary" data-testid="invoice-total">{editing.currency || "BOB"} {totals.total.toLocaleString()}</p></div>
          </div>

          {/* Pagos (solo en edición) */}
          {editing.id && (
            <PaymentsSection invoice={editing} onPay={(amt, m, ref) => addPayment(editing, amt, m, ref)} onRemove={(pid) => removePayment(editing, pid)} />
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-2 mt-4">
            <button onClick={save} data-testid="invoice-save" className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> Guardar</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-border rounded-sm text-sm">Cancelar</button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Código</th>
              <th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">Emisión</th>
              <th className="text-left p-3">Vence</th>
              <th className="text-right p-3">Total</th>
              <th className="text-right p-3">Cobrado</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Sin facturas todavía.</td></tr>}
            {items.map((inv) => (
              <tr key={inv.id} className="border-t border-border hover:bg-secondary/30">
                <td className="p-3 font-mono text-xs">{inv.code}</td>
                <td className="p-3 font-semibold">{inv.client_name || "—"}</td>
                <td className="p-3 text-xs">{inv.issue_date || "—"}</td>
                <td className="p-3 text-xs">{inv.due_date || "—"}</td>
                <td className="p-3 text-right">{inv.currency} {(Number(inv.total) || 0).toLocaleString()}</td>
                <td className="p-3 text-right text-emerald-500">{inv.currency} {(Number(inv.paid_total) || 0).toLocaleString()}</td>
                <td className="p-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${STATUS_CLS[inv.status]}`}>{STATUS_LABELS[inv.status] || inv.status}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing({ ...inv })} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-1 hover:border-primary"><Edit3 className="w-3 h-3" /> Abrir</button>
                  <button onClick={async () => {
                    const token = localStorage.getItem("influcas_token");
                    const r = await fetch(`${API}/admin/com/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                    if (!r.ok) { alert("Error PDF"); return; }
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = `${inv.code || "factura"}.pdf`; a.click(); URL.revokeObjectURL(url);
                  }} data-testid={`invoice-pdf-${inv.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-1 hover:border-primary"><Download className="w-3 h-3" /> PDF</button>
                  <button onClick={() => setShareInv(inv)} data-testid={`invoice-share-${inv.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-1 hover:border-primary"><Share2 className="w-3 h-3" /> Compartir</button>
                  <button onClick={() => del(inv.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm text-xs font-semibold hover:bg-destructive/10"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {shareInv && <ShareInvoiceModal invoice={shareInv} onClose={() => { setShareInv(null); load(); }} />}
    </div>
  );
}

function ShareInvoiceModal({ invoice, onClose }) {
  const [days, setDays] = useState(30);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (invoice.share_token) {
      const url = `${window.location.origin}/factura-publica/${invoice.id}?token=${invoice.share_token}`;
      setShare({ token: invoice.share_token, expires_at: invoice.share_expires_at, public_url: url });
    }
  }, [invoice]);

  const generate = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/admin/com/invoices/${invoice.id}/share`, { expires_in_days: Number(days) });
      const url = `${window.location.origin}/factura-publica/${invoice.id}?token=${r.token}`;
      setShare({ ...r, public_url: url });
    } catch (e) { setError(formatErr(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("¿Revocar el link público?")) return;
    await api.delete(`/admin/com/invoices/${invoice.id}/share`);
    setShare(null);
  };

  const copy = () => {
    if (!share?.public_url) return;
    navigator.clipboard.writeText(share.public_url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-sm w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <h3 className="font-display font-black text-xl mb-1">Compartir factura · {invoice.code}</h3>
        <p className="text-xs text-muted-foreground mb-4">Generá un link público con token. Cualquiera con el link puede ver la factura (sin información interna).</p>
        {!share ? (
          <>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Expira en (días)</label>
            <input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm mb-4" data-testid="share-expires-days" />
            {error && <p className="text-sm text-destructive mb-3">{error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 border border-border rounded-sm text-sm">Cancelar</button>
              <button onClick={generate} disabled={busy} data-testid="share-generate" className="px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold disabled:opacity-50">{busy ? "Generando..." : "Generar link"}</button>
            </div>
          </>
        ) : (
          <>
            <label className="text-[10px] uppercase tracking-widest font-bold mb-1 block">Link público</label>
            <div className="flex gap-2 mb-3">
              <input readOnly value={share.public_url} className="flex-1 h-10 px-3 bg-secondary/40 border border-border rounded-sm text-xs font-mono" data-testid="share-url" />
              <button onClick={copy} data-testid="share-copy" className="px-3 h-10 bg-primary text-primary-foreground rounded-sm text-xs font-semibold inline-flex items-center gap-1">
                {copied ? <><Check className="w-3 h-3" /> Copiado</> : <><Copy className="w-3 h-3" /> Copiar</>}
              </button>
            </div>
            {share.expires_at && <p className="text-xs text-muted-foreground mb-3">Expira: {share.expires_at.slice(0, 10)}</p>}
            <div className="flex justify-between gap-2">
              <button onClick={revoke} data-testid="share-revoke" className="px-4 py-2 border border-destructive/40 text-destructive rounded-sm text-sm font-semibold hover:bg-destructive/10">Revocar</button>
              <button onClick={onClose} className="px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold">Listo</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, cls = "" }) {
  return (
    <div className={`border rounded-sm p-4 ${cls || "border-border"}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{label}</p>
      <p className="font-display font-black text-xl tracking-tighter">{value}</p>
    </div>
  );
}

function PaymentsSection({ invoice, onPay, onRemove }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("transfer");
  const [reference, setReference] = useState("");
  const remaining = Math.max(0, (Number(invoice.total) || 0) - (Number(invoice.paid_total) || 0));
  return (
    <div className="border-t border-border pt-4 mt-4" data-testid="invoice-payments">
      <h4 className="font-display font-bold text-sm uppercase tracking-widest mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Pagos · {(invoice.payments || []).length}</h4>
      <div className="space-y-1 mb-3">
        {(invoice.payments || []).map((p) => (
          <div key={p.id} className="flex items-center gap-3 text-xs border border-border rounded-sm p-2">
            <span className="text-muted-foreground">{p.date}</span>
            <span className="font-bold text-emerald-500">{invoice.currency} {Number(p.amount).toLocaleString()}</span>
            <span className="text-muted-foreground">{p.method}</span>
            {p.reference && <span className="text-muted-foreground italic">{p.reference}</span>}
            <button onClick={() => onRemove(p.id)} className="ml-auto text-destructive hover:underline text-[10px]">Revertir</button>
          </div>
        ))}
        {(invoice.payments || []).length === 0 && <p className="text-xs italic text-muted-foreground">Sin pagos registrados.</p>}
      </div>
      <div className="flex gap-2 items-end" data-testid="invoice-add-payment">
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Monto a registrar</label>
          <input type="number" step="0.01" value={amount} placeholder={`Restante: ${remaining.toFixed(2)}`} onChange={(e) => setAmount(e.target.value)} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" />
        </div>
        <div className="w-32">
          <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Método</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm">
            <option value="transfer">Transferencia</option>
            <option value="cash">Efectivo</option>
            <option value="card">Tarjeta</option>
            <option value="qr">QR</option>
            <option value="other">Otro</option>
          </select>
        </div>
        <div className="w-40">
          <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Referencia</label>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="N° de op." className="w-full h-9 px-2 bg-background border border-border rounded-sm text-sm" />
        </div>
        <button onClick={() => { onPay(amount, method, reference); setAmount(""); setReference(""); }} disabled={!amount} data-testid="invoice-payment-submit" className="px-4 h-9 bg-emerald-600 text-white rounded-sm text-xs font-semibold disabled:opacity-50">
          + Registrar pago
        </button>
      </div>
    </div>
  );
}
